import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { api, formatApiErrorDetail } from "@/lib/api";

export const AdminLoginPage = ({ authLoading, onLogin, resolveUserDashboardPath, user }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useSeo({
    title: "Admin Login | SG Prop Portal",
    description: "Secure backend login for the owner admin and manager staff of SG Prop Portal.",
  });

  useEffect(() => {
    if (!authLoading && user) navigate(resolveUserDashboardPath(user), { replace: true });
  }, [authLoading, navigate, resolveUserDashboardPath, user]);

  if (!authLoading && user) return <Navigate replace to={resolveUserDashboardPath(user)} />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await api.post("/auth/login", form);
      onLogin(response.data);
      toast.success("Dashboard access granted");
      navigate(resolveUserDashboardPath(response.data.user), { replace: true });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#020813] px-6 py-10">
      <form className="luxury-panel w-full max-w-lg space-y-5 p-6 md:p-8" data-testid="admin-login-form" onSubmit={handleSubmit}>
        <p className="subtle-label">Backend Access</p>
        <h1 className="text-4xl font-semibold text-white" data-testid="admin-login-heading">
          Admin and manager sign in
        </h1>
        <p className="text-sm leading-7 text-slate-300">No public registration is enabled. Only the Admin can create new staff accounts inside the dashboard.</p>
        <input className="field-shell" data-testid="admin-login-email-input" onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Email" required type="email" value={form.email} />
        <input className="field-shell" data-testid="admin-login-password-input" onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="Password" required type="password" value={form.password} />
        <button className="gold-button w-full" data-testid="admin-login-submit-button" disabled={submitting} type="submit">
          {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
          Sign In
        </button>
      </form>
    </div>
  );
};